import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { useAuth } from "@/auth/use-auth";
import type { SpiffPaginatedResponse } from "@/api/dto/pagination";
import type { SpiffProcessInstanceRow } from "@/api/dto/process-instance";
import {
  buildCaseReportMetadata,
  type CaseListFilters,
} from "@/api/endpoints/build-case-report-metadata";
import { qk } from "@/api/query-keys";
import type { CaseListItem } from "@/domain/case/case";
import { CASE_CONFIG, type CaseConfig } from "@/domain/case/case-config";
import { toCaseListItem } from "@/domain/case/map-process-instance";

import { usePendingApprovals } from "@/features/approvals/use-approvals";

import { selectActionRequired } from "./select-action-required";

/**
 * 要対応を探すために取る件数。
 *
 * 差し戻し / 下書きは API だけでは絞り切れない（生ステータスが進行中・完了と
 * 同じ）ため、粗く取ってからクライアント側で判定する。一覧のページサイズとは
 * 独立にして、ページを送っても要対応の内容が変わらないようにする。
 */
export const ACTION_REQUIRED_FETCH_SIZE = 100;

/**
 * 要対応の対象になりうる状態だけを API に投げる（進行中・完了も混ざって返る）。
 *
 * 「関与した案件」では自分宛の承認待ちも要対応に含めるので、進行中も取る
 * （承認待ちの案件は非終了のまま）。
 */
const CANDIDATE_FILTERS: CaseListFilters = {
  keyword: "",
  statuses: ["returned", "draft"],
  processModelId: null,
  submittedFrom: null,
  submittedTo: null,
};

export type ActionRequiredResult = {
  readonly items: readonly CaseListItem[];
  /** 候補を取り切れていない可能性があるか（この先にも要対応があるかもしれない）。 */
  readonly hasMore: boolean;
};

/**
 * 「自分が動かさないと進まない」案件（差し戻し・下書き）。
 *
 * **自分が起票した案件だけ**を対象にする。`for-me` は関与案件を返すので、絞らないと
 * 承認しただけの他人の差し戻しまで「要対応」に並ぶ（実測で発生）。
 *
 * 一覧の絞り込み・検索・ページとは**独立に**取得する。一覧の結果から拾うと、
 * 「完了」で絞ったときに要対応が消える・2 ページ目で内容が変わるといった
 * 誤解を招く挙動になる。
 */
export function actionRequiredQueryOptions(
  client: ApiClient,
  params: {
    readonly config?: CaseConfig;
    /** 起票者の username。null なら絞らない（`use-my-applications` と同じ）。 */
    readonly initiatorUsername?: string | null;
    /**
     * 自分の番で止まっている案件の id（自分宛の承認待ち）。「関与した案件」の
     * ときだけ渡す。
     */
    readonly awaitingMe?: readonly number[];
  } = {},
) {
  const config = params.config ?? CASE_CONFIG;
  const initiatorUsername = params.initiatorUsername ?? null;

  const fetchActionRequired = async (): Promise<ActionRequiredResult> => {
    const response = await client.post<
      Partial<SpiffPaginatedResponse<SpiffProcessInstanceRow>>
    >(
      "/process-instances/for-me",
      {
        report_metadata: buildCaseReportMetadata(CANDIDATE_FILTERS, config, {
          initiatorUsername,
        }),
      },
      { query: { page: 1, per_page: ACTION_REQUIRED_FETCH_SIZE } },
    );

    const rows = response.results ?? [];
    const items = selectActionRequired(
      rows.map((row) => toCaseListItem(row, config)),
      { awaitingMe: params.awaitingMe },
    );

    return {
      items,
      hasMore: (response.pagination?.total ?? 0) > rows.length,
    };
  };

  return queryOptions({
    queryKey: qk.processInstances.forMe({
      statuses: CANDIDATE_FILTERS.statuses,
      perPage: ACTION_REQUIRED_FETCH_SIZE,
      scope: "action-required",
      initiator: initiatorUsername,
      // 承認待ちを混ぜるかで結果が変わる。key はプレーンな値だけにする規約なので
      // id を並べた文字列にする
      awaitingMe: [...(params.awaitingMe ?? [])]
        .sort((a, b) => a - b)
        .join(","),
    }),
    queryFn: fetchActionRequired,
    staleTime: 30_000,
  });
}

export function useActionRequired(
  params: { readonly involved?: boolean } = {},
): UseQueryResult<ActionRequiredResult> {
  const { user } = useAuth();
  // 「関与した案件」では自分宛の承認待ちも要対応に含める（一覧の範囲に合わせる）
  const approvals = usePendingApprovals();
  const awaitingMe =
    params.involved === true
      ? (approvals.data ?? []).map((item) => item.caseId)
      : undefined;

  return useQuery(
    actionRequiredQueryOptions(useApiClient(), {
      // 関与モードでは他人の案件（自分が承認者）も対象なので起票者では絞らない
      initiatorUsername:
        params.involved === true ? null : (user?.username ?? null),
      awaitingMe,
    }),
  );
}
